// ---- the grass ----
// What both games stand on, painted at load: a canvas of tufts tiled across
// the field, and under it a slow wash of lighter and deeper greens laid on the
// plane's own vertices, so the tile's repeat never lines up into a grid. It is
// drawn, not photographed, because everything standing on it is too — a
// photo's dirt and shadow would be the only real thing in the picture — and
// because a canvas is nothing to download.
//
// Seeded, so it is the same lawn on every load: the tufts round Tata's table
// are where they were yesterday.
import * as THREE from 'three';

const S=1024;                                   // the canvas, in pixels
const TILE=4;                                   // how much ground one canvas covers, in units
// mulberry32: a little seeded random, so the lawn does not change between loads
function seeded(a:number){
  return ()=>{a=(a+0x6D2B79F5)|0;let t=Math.imul(a^(a>>>15),1|a);t=(t+Math.imul(t^(t>>>7),61|t))^t;return((t^(t>>>14))>>>0)/4294967296;};
}
// Draw f at (x,y) and again wherever a thing m across would poke over an edge,
// shifted a canvas over, so the far side carries what the near side cut off and
// the tile meets its neighbour with no seam.
function wrapped(x:number,y:number,m:number,f:(x:number,y:number)=>void){
  for(const dx of [0,-S,S])for(const dy of [0,-S,S]){
    const u=x+dx, v=y+dy;
    if(u>-m&&u<S+m&&v>-m&&v<S+m)f(u,v);
  }
}
// One blade, seen from above: a curved sliver from its root at (x,y) out to its
// tip, w wide at the root. bend swings the tip off the line it set out on.
function blade(p:CanvasPath,x:number,y:number,a:number,L:number,w:number,bend:number){
  const px=-Math.sin(a)*w/2, py=Math.cos(a)*w/2;
  const cx=x+Math.cos(a)*L*0.55, cy=y+Math.sin(a)*L*0.55;
  const tx=x+Math.cos(a+bend)*L, ty=y+Math.sin(a+bend)*L;
  p.moveTo(x+px,y+py); p.quadraticCurveTo(cx+px*0.5,cy+py*0.5,tx,ty);
  p.quadraticCurveTo(cx-px*0.5,cy-py*0.5,x-px,y-py); p.closePath();
}

// The ground's base, and the greens a tuft is drawn in, darkest first: they go
// down a colour at a time, so every lighter blade lies over every darker one,
// which is what the light does to a lawn anyway.
const BASE='#90D46B';
const BLADES=['#6DB251','#7CBF5A','#8BCB64','#A6DC7E','#BDE893'];
function paint(){
  const cv=document.createElement('canvas'); cv.width=cv.height=S;
  const c=cv.getContext('2d')!, r=seeded(20260916);
  c.fillStyle=BASE; c.fillRect(0,0,S,S);
  // soft patches, a little sunnier or a little deeper, so the ground between
  // the tufts is not one flat colour. Faint, and many: one strong patch is a
  // landmark, and a landmark every TILE units is a grid.
  for(let i=0;i<90;i++){
    const x=r()*S, y=r()*S, R=50+r()*110, warm=r()<0.5;
    wrapped(x,y,R,(u,v)=>{
      const g=c.createRadialGradient(u,v,0,u,v,R);
      g.addColorStop(0,warm?'rgba(178,222,112,0.16)':'rgba(84,160,74,0.10)'); g.addColorStop(1,'rgba(0,0,0,0)');
      c.fillStyle=g; c.fillRect(u-R,v-R,2*R,2*R);
    });
  }
  // the tufts: a few blades fanned out from one root, seen from above, the
  // under ones darker than the top ones. Each blade is a fill of its own:
  // twelve thousand fills took a sixth of the time of five paths of two
  // thousand blades each, which cost 140 ms and held up the models' download.
  const shades:(()=>void)[][]=BLADES.map(()=>[]);
  for(let i=0;i<2600;i++){
    const x=r()*S, y=r()*S, n=3+((r()*4)|0), a0=r()*2*Math.PI, L0=18+r()*22, top=r()<0.55?2:1;
    // drawn before it is placed, so a tuft cut by an edge is the same tuft on both sides
    const bs=Array.from({length:n},(_,k)=>({
      shade:Math.min(BLADES.length-1,Math.max(0,top+(k*2>=n?1:-1)+((r()*2)|0))),
      a:a0+k*2*Math.PI/n+(r()-0.5)*0.9, L:L0*(0.7+r()*0.6), w:4+r()*4, bend:(r()-0.5)*0.8}));
    wrapped(x,y,L0*1.3,(u,v)=>{for(const b of bs)shades[b.shade].push(()=>blade(c,u,v,b.a,b.L,b.w,b.bend));});
  }
  shades.forEach((l,i)=>{c.fillStyle=BLADES[i]; for(const f of l){c.beginPath(); f(); c.fill();}});
  return cv;
}

export function grassMaterial(aniso:number){
  const t=new THREE.CanvasTexture(paint());
  t.colorSpace=THREE.SRGBColorSpace; t.wrapS=t.wrapT=THREE.RepeatWrapping;
  // The camera skims the field at a low angle, which is where a lawn smears
  // to a flat green without it.
  t.anisotropy=Math.min(8,aniso);
  return new THREE.MeshLambertMaterial({map:t,vertexColors:true});
}
// A plane `size` across, the tile repeated over it, and a wash of colour laid
// on its vertices: three slow waves at angles to each other, so a patch a few
// tiles wide comes out a touch yellower or a touch bluer and no two tiles of
// the field look alike from where the camera stands.
export function grassGeometry(size:number){
  const seg=Math.round(size/2), g=new THREE.PlaneGeometry(size,size,seg,seg);
  const uv=g.attributes.uv as THREE.BufferAttribute, pos=g.attributes.position as THREE.BufferAttribute;
  for(let i=0;i<uv.count;i++)uv.setXY(i,uv.getX(i)*size/TILE,uv.getY(i)*size/TILE);
  const col=new Float32Array(pos.count*3);
  for(let i=0;i<pos.count;i++){
    const x=pos.getX(i), y=pos.getY(i);
    const n=(Math.sin(0.23*x+0.11*y+1.3)*0.5+Math.sin(-0.13*x+0.29*y+4.1)*0.35+Math.sin(0.07*x-0.09*y+2.2)*0.45)/1.3;
    const k=Math.max(-1,Math.min(1,n));
    // n>0 sunnier (redder, less blue), n<0 deeper (darker, a hint of blue)
    col[i*3]=1+0.10*k; col[i*3+1]=1+0.03*k; col[i*3+2]=1-0.10*k;
    if(k<0){col[i*3]+=0.06*k; col[i*3+1]+=0.08*k;}
  }
  g.setAttribute('color',new THREE.BufferAttribute(col,3));
  return g;
}
